import { Component, Input, ChangeDetectorRef, OnInit, OnDestroy, OutputRefSubscription, inject, input, output, viewChild } from '@angular/core';
import { SelectionType } from '../../types/selection.type';
import { TableColumn } from '../../types/table-column.type';
import { columnsByPin, columnGroupWidths } from '../../utils/column';
import { RowHeightCache } from '../../utils/row-height-cache';
import { translateXY } from '../../utils/translate';
import { DatatableRowDetailDirective } from '../row-detail/row-detail.directive';
import { ScrollerComponent } from './scroller.component';
import { DataTableBodyRowComponent } from './body-row.component';
import { DataTableRowWrapperComponent } from './body-row-wrapper.component';
import { DataTableSummaryRowComponent } from './summary/summary-row.component';
import { DataTableSelectionComponent } from './selection.component';
import { ProgressBarComponent } from './progress-bar.component';
import { DatatableGroupHeaderDirective } from './body-group-header.directive';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type DatatableToggleEvent = {
  type: 'row' | 'group' | 'all';
  value: unknown;
};

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'datatable-body',
  template: `
    @if (loadingIndicator()) {
      <datatable-progress />
    }
    <datatable-selection #selector
                         [selected]="selected()"
                         [rows]="rows"
                         [selectCheck]="selectCheck()"
                         [selectEnabled]="selectEnabled"
                         [selectionType]="selectionType()"
                         [rowIdentity]="rowIdentity()"
                         (select)="select.emit($event)"
                         (activate)="activate.emit($event)">
      @if (rows?.length) {
        <datatable-scroller [scrollbarV]="scrollbarV()"
                            [scrollbarH]="scrollbarH()"
                            [scrollHeight]="scrollHeight"
                            [scrollWidth]="columnGroupWidths?.total"
                            (scroll)="onBodyScroll($event)">
          @if (summaryRow() && summaryPosition() === 'top') {
            <datatable-summary-row [rowHeight]="summaryHeight()"
                                   [offsetX]="offsetX()"
                                   [innerWidth]="innerWidth()"
                                   [rows]="rows"
                                   [columns]="columns" />
          }
          @for (group of temp; track rowTrackingFn(i, group); let i = $index) {
            <datatable-row-wrapper [groupedRows]="groupedRows()"
                                   [innerWidth]="innerWidth()"
                                   [style]="getRowsStyles(group)"
                                   [rowDetail]="rowDetail()"
                                   [groupHeader]="groupHeader()"
                                   [offsetX]="offsetX()"
                                   [detailRowHeight]="getDetailRowHeight(group && group[i], i)"
                                   [row]="group"
                                   [expanded]="getRowExpanded(group)"
                                   [rowIndex]="getRowIndex(group && group[i])"
                                   (rowContextmenu)="rowContextmenu.emit($event)">
              @if (!groupedRows()) {
                <datatable-body-row role="row"
                                    tabindex="-1"
                                    [isSelected]="selector.getRowSelected(group)"
                                    [innerWidth]="innerWidth()"
                                    [offsetX]="offsetX()"
                                    [columns]="columns"
                                    [rowHeight]="getRowHeight(group)"
                                    [row]="group"
                                    [rowIndex]="getRowIndex(group)"
                                    [expanded]="getRowExpanded(group)"
                                    [rowClass]="rowClass()"
                                    [displayCheck]="displayCheck()"
                                    [treeStatus]="$any(group?.treeStatus)"
                                    (treeAction)="onTreeAction(group)"
                                    (activate)="selector.onActivate($any($event), indexes.first + i)" />
              } @else {

                @for (row of $any(group.value); track rowTrackingFn(i, row); let i = $index) {
                  <datatable-body-row role="row"
                                      tabindex="-1"
                                      [isSelected]="selector.getRowSelected(row)"
                                      [innerWidth]="innerWidth()"
                                      [offsetX]="offsetX()"
                                      [columns]="columns"
                                      [rowHeight]="getRowHeight(row)"
                                      [row]="row"
                                      [group]="group.value"
                                      [rowIndex]="getRowIndex(row)"
                                      [expanded]="getRowExpanded(row)"
                                      [rowClass]="rowClass()"
                                      (activate)="selector.onActivate($any($event), i)" />
                }
              }
            </datatable-row-wrapper>
          }
          @if (summaryRow() && summaryPosition() === 'bottom') {
            <datatable-summary-row role="row"
                                   [style]="getBottomSummaryRowStyles()"
                                   [rowHeight]="summaryHeight()"
                                   [offsetX]="offsetX()"
                                   [innerWidth]="innerWidth()"
                                   [rows]="rows"
                                   [columns]="columns" />
          }
        </datatable-scroller>
      }
      @if (!rows?.length && !loadingIndicator()) {
        <div class="empty-row" [innerHTML]="emptyMessage()"></div>
      }
    </datatable-selection>
  `,
  host: {
    class: 'datatable-body',
    '[style.width]': 'bodyWidth',
    '[style.height]': 'bodyHeight'
  },
  imports: [
    ProgressBarComponent,
    DataTableSelectionComponent,
    ScrollerComponent,
    DataTableSummaryRowComponent,
    DataTableRowWrapperComponent,
    DataTableBodyRowComponent
  ]
})
export class DataTableBodyComponent implements OnInit, OnDestroy {
  private readonly cd = inject(ChangeDetectorRef);

  readonly scrollbarV = input<boolean>(undefined);
  readonly scrollbarH = input<boolean>(undefined);
  readonly loadingIndicator = input<boolean>(undefined);
  readonly externalPaging = input<boolean>(undefined);
  readonly rowHeight = input<number | 'auto' | ((row?: unknown) => number)>(undefined);
  readonly offsetX = input<number>(undefined);
  readonly emptyMessage = input<string>(undefined);
  readonly selectionType = input<SelectionType>(undefined);
  readonly selected = input<Array<unknown>>([]);
  readonly rowIdentity = input<(row: unknown) => unknown>(undefined);
  readonly rowDetail = input<DatatableRowDetailDirective>(undefined);
  readonly groupHeader = input<DatatableGroupHeaderDirective>(undefined);
  readonly selectCheck = input<((row: unknown) => boolean) | undefined>(undefined);
  readonly displayCheck = input<(row: unknown, column?: TableColumn, value?: unknown) => boolean>(undefined);
  readonly trackByProp = input<string>(undefined);
  readonly rowClass = input<(row: unknown) => string | Record<string, boolean>>(undefined);
  readonly groupedRows = input<Array<Record<string, unknown>>>(undefined);
  readonly groupExpansionDefault = input<boolean>(undefined);
  readonly innerWidth = input<number>(undefined);
  readonly groupRowsBy = input<string>(undefined);
  readonly virtualization = input<boolean>(undefined);
  readonly summaryRow = input<boolean>(undefined);
  readonly summaryPosition = input<string>(undefined);
  readonly summaryHeight = input<number | 'auto'>(undefined);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set pageSize(val: number) {
    this._pageSize = val;
    this.recalcLayout();
  }

  get pageSize(): number {
    return this._pageSize;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set rows(val: Array<Record<string, unknown>>) {
    this._rows = val;
    this.recalcLayout();
  }

  get rows(): Array<Record<string, unknown>> {
    return this._rows;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set columns(val: Array<TableColumn>) {
    this._columns = val;
    const colsByPin = columnsByPin(val);
    this.columnGroupWidths = columnGroupWidths(colsByPin, val);
  }

  get columns(): Array<TableColumn> {
    return this._columns;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set offset(val: number) {
    this._offset = val;
    const scrollbarV = this.scrollbarV();
    if (!scrollbarV || (scrollbarV && !this.virtualization())) {
      this.recalcLayout();
    }
  }

  get offset(): number {
    return this._offset;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set rowCount(val: number) {
    this._rowCount = val;
    this.recalcLayout();
  }

  get rowCount(): number {
    return this._rowCount;
  }

  get bodyWidth(): string {
    if (this.scrollbarH()) {
      return this.innerWidth() + 'px';
    } else {
      return '100%';
    }
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  set bodyHeight(val: number) {
    if (this.scrollbarV()) {
      this._bodyHeight = val + 'px';
    } else {
      this._bodyHeight = 'auto';
    }

    this.recalcLayout();
  }

  get bodyHeight(): string {
    return this._bodyHeight;
  }

  readonly scroll = output<{ offsetY: number; offsetX: number }>();
  readonly page = output<{ offset: number }>();
  readonly activate = output<unknown>();
  readonly select = output<{ selected: Array<unknown> }>();
  readonly detailToggle = output<unknown>();
  readonly rowContextmenu = output<{
    event: MouseEvent;
    row: unknown;
}>();

  readonly treeAction = output<{ row: unknown }>();

  readonly scroller = viewChild(ScrollerComponent);

  /**
   * Returns if selection is enabled.
   */
  get selectEnabled(): boolean {
    return !!this.selectionType();
  }

  /**
   * Property that would calculate the height of scroll bar
   * based on the row heights cache for virtual scroll and virtualization. Other scenarios
   * calculate scroll height automatically (as height will be undefined).
   */
  get scrollHeight(): number | undefined {
    if (this.scrollbarV() && this.virtualization() && this.rowCount) {
      return this.rowHeightsCache.query(this.rowCount - 1);
    }
    // avoid TS7030: Not all code paths return a value.
    return undefined;
  }

  rowHeightsCache: RowHeightCache = new RowHeightCache();
  temp: Array<Record<string, unknown>> = [];
  offsetY = 0;
  indexes: { first: number; last: number } = { first: 0, last: 0 };
  columnGroupWidths: { left: number; center: number; right: number; total: number };
  columnGroupWidthsWithoutGroup: { left: number; center: number; right: number; total: number };
  rowTrackingFn: (_index: number, row: unknown) => unknown;
  listener: OutputRefSubscription;
  rowIndexes: WeakMap<object, unknown> = new WeakMap<object, unknown>();
  rowExpansions: Array<unknown> = [];

  _rows: Array<Record<string, unknown>>;
  _bodyHeight: string;
  _columns: Array<TableColumn>;
  _rowCount: number;
  _offset: number;
  _pageSize: number;

  /**
   * Creates an instance of DataTableBodyComponent.
   */
  constructor() {
    // declare fn here so we can get access to the `this` property
    this.rowTrackingFn = (_index: number, row: unknown): unknown => {
      const idx = this.getRowIndex(row);
      const trackByProp = this.trackByProp();
      if (trackByProp) {
        return (row as Record<string, unknown>)[trackByProp];
      } else {
        return idx;
      }
    };
  }

  /**
   * Called after the constructor, initializing input properties
   */
  ngOnInit(): void {
    const rowDetail = this.rowDetail();
    if (rowDetail) {
      this.listener = rowDetail.toggle.subscribe((evt: unknown) => {
        const { type, value }: DatatableToggleEvent = evt as DatatableToggleEvent;
        if (type === 'row') {
          this.toggleRowExpansion(value);
        }
        if (type === 'all') {
          this.toggleAllRows(value as boolean);
        }

        this.updateIndexes();
        this.updateRows();
        this.cd.markForCheck();
      });
    }

    const groupHeader = this.groupHeader();
    if (groupHeader) {
      this.listener = groupHeader.toggle.subscribe((evt: unknown) => {
        const { type, value }: DatatableToggleEvent = evt as DatatableToggleEvent;
        if (type === 'group') {
          this.toggleRowExpansion(value);
        }
        if (type === 'all') {
          this.toggleAllRows(value as boolean);
        }

        this.updateIndexes();
        this.updateRows();
        this.cd.markForCheck();
      });
    }
  }

  /**
   * Called once, before the instance is destroyed.
   */
  ngOnDestroy(): void {
    if (this.rowDetail() || this.groupHeader()) {
      this.listener?.unsubscribe();
    }
  }

  /**
   * Updates the Y offset given a new offset.
   */
  updateOffsetY(offset?: number): void {
    // scroller is missing on empty table
    const scroller = this.scroller();
    if (!scroller) {
      return;
    }

    const scrollbarV = this.scrollbarV();
    const virtualization = this.virtualization();
    if (scrollbarV && virtualization && offset) {
      // First get the row Index that we need to move to.
      const rowIndex = this.pageSize * offset;
      offset = this.rowHeightsCache.query(rowIndex - 1);
    } else if (scrollbarV && !virtualization) {
      offset = 0;
    }

    scroller.setOffset(offset || 0);
  }

  /**
   * Body was scrolled, this is mainly useful for
   * when a user is server-side pagination via virtual scroll.
   */
  onBodyScroll(event: unknown): void {
    const scrollEvt = event as { scrollYPos: number; scrollXPos: number; direction: string };
    const scrollYPos: number = scrollEvt.scrollYPos;
    const scrollXPos: number = scrollEvt.scrollXPos;

    // if scroll change, trigger update
    // this is mainly used for header cell positions
    if (this.offsetY !== scrollYPos || this.offsetX() !== scrollXPos) {
      this.scroll.emit({
        offsetY: scrollYPos,
        offsetX: scrollXPos
      });
    }

    this.offsetY = scrollYPos;
    this.updateIndexes();
    this.updatePage(scrollEvt.direction);
    this.updateRows();
  }

  /**
   * Updates the page given a direction.
   */
  updatePage(direction: string): void {
    let offset = this.indexes.first / this.pageSize;
    if (direction === 'up') {
      offset = Math.ceil(offset);
    } else if (direction === 'down') {
      offset = Math.floor(offset);
    }

    if (direction !== undefined && !isNaN(offset)) {
      this.page.emit({ offset });
    }
  }

  /**
   * Updates the rows in the view port
   */
  updateRows(): void {
    const { first, last }: { first: number; last: number } = this.indexes;
    let rowIndex = first;
    let idx = 0;
    const temp: Array<Record<string, unknown>> = [];

    // if grouprowsby has been specified treat row paging
    // parameters as group paging parameters ie if limit 10 has been
    // specified treat it as 10 groups rather than 10 rows
    const groupedRows = this.groupedRows();
    if (groupedRows) {
      while (rowIndex < last && rowIndex < groupedRows.length) {
        // Add the groups into this page
        const group = groupedRows[rowIndex];
        this.rowIndexes.set(group, rowIndex);

        if (group['value']) {
          // add indexes for each group item
          (group['value'] as Array<unknown>).forEach((g: unknown, i: number) => {
            const groupIdx = `${rowIndex}-${i}`;
            this.rowIndexes.set(g as object, groupIdx);
          });
        }
        temp[idx] = group;
        idx++;

        // Group index in this context
        rowIndex++;
      }
    } else {
      while (rowIndex < last && rowIndex < this.rowCount) {
        const row = this.rows[rowIndex];

        if (row) {
          // add indexes for each row
          this.rowIndexes.set(row, rowIndex);
          temp[idx] = row;
        }

        idx++;
        rowIndex++;
      }
    }

    this.temp = temp;
  }

  /**
   * Get the row height
   */
  getRowHeight(row: unknown): number {
    // if its a function return it
    const rowHeight = this.rowHeight();
    if (typeof rowHeight === 'function') {
      return rowHeight(row);
    }

    return rowHeight as number;
  }

  /**
   * @param group the group with all rows
   */
  getGroupHeight(group: { value?: Array<unknown>; [key: string]: unknown }): number {
    let rowHeight = 0;

    if (group.value) {
      for (const groupItem of group.value) {
        rowHeight += this.getRowAndDetailHeight(groupItem);
      }
    }

    return rowHeight;
  }

  /**
   * Calculate row height based on the expanded state of the row.
   */
  getRowAndDetailHeight(row: unknown): number {
    let rowHeight = this.getRowHeight(row);
    const expanded = this.getRowExpanded(row);

    // Adding detail row height if its expanded.
    if (expanded) {
      rowHeight += this.getDetailRowHeight(row);
    }

    return rowHeight;
  }

  /**
   * Get the height of the detail row.
   */
  getDetailRowHeight = (row?: unknown, index?: number): number => {
    const rowDetail = this.rowDetail();
    if (!rowDetail) {
      return 0;
    }
    const rowHeight = rowDetail.rowHeight();
    return typeof rowHeight === 'function' ? rowHeight(row, index) : (rowHeight as number);
  };

  /**
   * Calculates the styles for the row so that the rows can be moved in 2D space
   * during virtual scroll inside the DOM.   In the below case the Y position is
   * manipulated.   As an example, if the height of row 0 is 30 px and row 1 is
   * 100 px then following styles are generated:
   *
   * transform: translate3d(0px, 0px, 0px);    ->  row0
   * transform: translate3d(0px, 30px, 0px);   ->  row1
   * transform: translate3d(0px, 130px, 0px);  ->  row2
   *
   * Row heights have to be calculated based on the row heights cache as we wont
   * be able to determine which row is of what height before hand.  In the above
   * case the positionY of the translate3d for row2 would be the sum of all the
   * heights of the rows before it (i.e. row0 and row1).
   *
   * @param rows the row that needs to be placed in the 2D space.
   * @returns the CSS3 style to be applied
   *
   * @memberOf DataTableBodyComponent
   */
  getRowsStyles(rows: unknown): Record<string, string | number> {
    const styles: Record<string, string | number> = {};

    // only add styles for the group if there is a group
    const groupedRows = this.groupedRows();
    if (groupedRows) {
      styles['width'] = this.columnGroupWidths.total;
    }

    if (this.scrollbarV() && this.virtualization()) {
      let idx: number;

      if (groupedRows) {
        // Get the latest row rowindex in a group
        const rowsArr = rows as Array<unknown>;
        const row = rowsArr[rowsArr.length - 1];
        idx = row ? this.getRowIndex(row) : 0;
      } else {
        idx = this.getRowIndex(rows);
      }

      // const pos = idx * rowHeight;
      // The position of this row would be the sum of all row heights
      // until the previous row position.
      const pos = this.rowHeightsCache.query(idx - 1);

      translateXY(styles, 0, pos);
    }

    return styles;
  }

  /**
   * Calculate bottom summary row offset for scrollbar mode.
   * For more information about cache and offset calculation
   * see description for `getRowsStyles` method
   *
   * @returns the CSS3 style to be applied
   *
   * @memberOf DataTableBodyComponent
   */
  getBottomSummaryRowStyles(): Record<string, string | number> | null {
    if (!this.scrollbarV() || !this.rows || !this.rows.length) {
      return null;
    }

    const styles = { position: 'absolute' };
    const pos = this.rowHeightsCache.query(this.rows.length - 1);

    translateXY(styles, 0, pos);

    return styles;
  }

  /**
   * Hides the loading indicator.
   * NOTE: `loadingIndicator` is a signal input owned by the parent; this
   * helper is currently unused. Kept as a no-op placeholder to avoid
   * breaking any external references.
   */
  hideIndicator(): void {
    // No-op: loadingIndicator is controlled by the parent via [loadingIndicator] input.
  }

  /**
   * Updates the index of the rows in the viewport
   */
  updateIndexes(): void {
    let first = 0;
    let last: number;

    if (this.scrollbarV()) {
      if (this.virtualization()) {
        // Calculation of the first and last indexes will be based on where the
        // scrollY position would be at.  The last index would be the one
        // that shows up inside the view port the last.
        const height = parseInt(this.bodyHeight, 0);
        first = this.rowHeightsCache.getRowIndex(this.offsetY);
        last = this.rowHeightsCache.getRowIndex(height + this.offsetY) + 1;
      } else {
        // If virtual rows are not needed
        // We render all in one go
        first = 0;
        last = this.rowCount;
      }
    } else {
      // The server is handling paging and will pass an array that begins with the
      // element at a specified offset.  first should always be 0 with external paging.
      if (!this.externalPaging()) {
        first = Math.max(this.offset * this.pageSize, 0);
      }
      last = Math.min(first + this.pageSize, this.rowCount);
    }

    this.indexes = { first, last };
  }

  /**
   * Refreshes the full Row Height cache.  Should be used
   * when the entire row array state has changed.
   */
  refreshRowHeightCache(): void {
    const scrollbarV = this.scrollbarV();
    if (!scrollbarV || (scrollbarV && !this.virtualization())) {
      return;
    }

    // clear the previous row height cache if already present.
    // this is useful during sorts, filters where the state of the
    // rows array is changed.
    this.rowHeightsCache.clearCache();

    // Initialize the tree only if there are rows inside the tree.
    if (this.rows && this.rows.length) {
      const rowExpansions = new Set();
      for (const row of this.rows) {
        if (this.getRowExpanded(row)) {
          rowExpansions.add(row);
        }
      }

      this.rowHeightsCache.initCache({
        rows: this.rows,
        rowHeight: this.rowHeight(),
        detailRowHeight: this.getDetailRowHeight,
        externalVirtual: scrollbarV && this.externalPaging(),
        rowCount: this.rowCount,
        rowIndexes: this.rowIndexes,
        rowExpansions
      });
    }
  }

  /**
   * Gets the index for the view port
   */
  getAdjustedViewPortIndex(): number {
    // Capture the row index of the first row that is visible on the viewport.
    // If the scroll bar is just below the row which is highlighted then make that as the
    // first index.
    const viewPortFirstRowIndex = this.indexes.first;

    if (this.scrollbarV() && this.virtualization()) {
      const offsetScroll = this.rowHeightsCache.query(viewPortFirstRowIndex - 1);
      return offsetScroll <= this.offsetY ? viewPortFirstRowIndex - 1 : viewPortFirstRowIndex;
    }

    return viewPortFirstRowIndex;
  }

  /**
   * Toggle the Expansion of the row i.e. if the row is expanded then it will
   * collapse and vice versa.   Note that the expanded status is stored as
   * a part of the row object itself as we have to preserve the expanded row
   * status in case of sorting and filtering of the row set.
   */
  toggleRowExpansion(row: unknown): void {
    // Capture the row index of the first row that is visible on the viewport.
    const viewPortFirstRowIndex = this.getAdjustedViewPortIndex();
    const rowExpandedIdx = this.getRowExpandedIdx(row, this.rowExpansions);
    const expanded = rowExpandedIdx > -1;

    // If the detailRowHeight is auto --> only in case of non-virtualized scroll
    if (this.scrollbarV() && this.virtualization()) {
      const detailRowHeight = this.getDetailRowHeight(row) * (expanded ? -1 : 1);
      // const idx = this.rowIndexes.get(row) || 0;
      const idx = this.getRowIndex(row);
      this.rowHeightsCache.update(idx, detailRowHeight);
    }

    // Update the toggled row and update thive nevere heights in the cache.
    if (expanded) {
      this.rowExpansions.splice(rowExpandedIdx, 1);
    } else {
      this.rowExpansions.push(row);
    }

    this.detailToggle.emit({
      rows: [row],
      currentIndex: viewPortFirstRowIndex
    });
  }

  /**
   * Expand/Collapse all the rows no matter what their state is.
   */
  toggleAllRows(expanded: boolean): void {
    // clear prev expansions
    this.rowExpansions = [];

    // Capture the row index of the first row that is visible on the viewport.
    const viewPortFirstRowIndex = this.getAdjustedViewPortIndex();

    if (expanded) {
      for (const row of this.rows) {
        this.rowExpansions.push(row);
      }
    }

    if (this.scrollbarV()) {
      // Refresh the full row heights cache since every row was affected.
      this.recalcLayout();
    }

    // Emit all rows that have been expanded.
    this.detailToggle.emit({
      rows: this.rows,
      currentIndex: viewPortFirstRowIndex
    });
  }

  /**
   * Recalculates the table
   */
  recalcLayout(): void {
    this.refreshRowHeightCache();
    this.updateIndexes();
    this.updateRows();
  }

  /**
   * Tracks the column
   */
  columnTrackingFn(_index: number, column: { $$id?: string }): string {
    return column.$$id;
  }

  /**
   * Gets the row pinning group styles
   */
  stylesByGroup(group: string): Record<string, string> {
    const widths = this.columnGroupWidths;
    const offsetX = this.offsetX();

    const styles = {
      width: `${(widths as unknown as Record<string, number>)[group]}px`
    };

    if (group === 'left') {
      translateXY(styles, offsetX, 0);
    } else if (group === 'right') {
      const bodyWidth = parseInt(this.innerWidth() + '', 0);
      const totalDiff = widths.total - bodyWidth;
      const offsetDiff = totalDiff - offsetX;
      const offset = offsetDiff * -1;
      translateXY(styles, offset, 0);
    }

    return styles;
  }

  /**
   * Returns if the row was expanded and set default row expansion when row expansion is empty
   */
  getRowExpanded(row: unknown): boolean {
    if (this.rowExpansions.length === 0 && this.groupExpansionDefault()) {
      for (const group of this.groupedRows()) {
        this.rowExpansions.push(group);
      }
    }

    return this.getRowExpandedIdx(row, this.rowExpansions) > -1;
  }

  getRowExpandedIdx(row: unknown, expanded: Array<unknown>): number {
    if (!expanded || !expanded.length) {
      return -1;
    }

    const rowId = this.rowIdentity()(row);
    return expanded.findIndex((r: unknown) => {
      const id = this.rowIdentity()(r);
      return id === rowId;
    });
  }

  /**
   * Gets the row index given a row
   */
  getRowIndex(row: unknown): number {
    return (this.rowIndexes.get(row as object) as number) || 0;
  }

  onTreeAction(row: unknown): void {
    this.treeAction.emit({ row });
  }
}
